// @ts-check

// eslint-disable-next-line no-unused-vars
import React, { useEffect, useState } from 'react'
import { Input } from '@artprov/design-system'
import { maskMonthYear, validateMonthYear } from './date.utils'
import PropTypes from 'prop-types'
/**
 * InputMonthYear component for handling month/year input with validation and formatting.
 *
 * @param {Object}  props - The properties object.
 * @param {string} [props.placeholder='YYYY/MM'] - The placeholder text for the input field.
 * @param {Function} [props.onChange=() => {}] - The callback function to handle input changes.
 * @param {Function} [props.onValidate=() => {}] - devuelve true en caso de que la validacion interna sea correcta, false en caso contrario.
 * @param {string} [props.value] -  The current value of the input field.
 * @param {number} [props.minYear] - The minimum allowable year.
 * @param {number} [props.maxYear] - The maximum allowable year.
 * @param {string} [props.label=''] - The label for the input field.
 * @param {boolean} [props.isOptional=false] - Whether the input field is optional.
 * @param {boolean} [props.isRequired=false] - Whether the input field is required.
 * @param {string} [props.info=''] - Additional information for the input field.
 * @param {Array<{ variant: 'info' | 'error' | 'success', text: string }>} [props.hints=[{ variant: 'info', text: 'Hint por defecto' }]] - Array of hint objects to display.
 * @param {string} [props.classNameContainer=''] - Additional class name for the container.
 * @param {string} [props.classNameContainerInput=''] - Additional class name for the input container.
 * @param {string} [props.className=''] - Additional class name for the input field.
 *
 * @returns {JSX.Element} The rendered InputMonthYear component.
 */
export const InputMonthYear = ({
  placeholder = 'AAAA/MM',
  // eslint-disable-next-line no-unused-vars
  onChange = (_) => {},
  onValidate = () => {},
  minYear,
  maxYear,
  label = '',
  value = '',
  isOptional = false,
  isRequired = false,
  info = '',
  hints = [
    {
      variant: 'info',
      text: 'Hint por defecto',
    },
  ],
  classNameContainer = '',
  classNameContainerInput = '',
  className = '',
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(value)
  const [status, setStatus] = useState('default') // default, error, success
  const [hintText, setHintText] = useState('Ingresá año y mes de las remuneraciones') // Mensaje por defecto

  // Sync the component's status with the hints prop.
  // When hints contains an error variant (e.g. from API validation failure),
  // set the status to 'error' so the input shows the error visual state.
  useEffect(() => {
    const hasErrorHint = hints.some((hint) => hint.variant === 'error')
    if (hasErrorHint) {
      setStatus('error')
    }
  }, [hints])

  const hintsArray = [
    // Only add the component's own error hint when the hints prop doesn't
    // already contain one (to avoid duplicates when the parent sets error hints
    // via API after validation failure)
    ...(status === 'error' && !hints.some((h) => h.variant === 'error') ? [{ variant: 'error', text: hintText }] : []),
    ...(status === 'default' ? [{ variant: 'info', text: hintText }] : []),
    ...hints,
  ].filter(
    // When the component's own status is 'error', show only error hints
    // Otherwise, show all hints (errors from props are cleared by the parent
    // when the user modifies the input)
    (hint) => status !== 'error' || hint.variant === 'error'
  )

  const handleKeyDown = (e) => {
    // Allow numbers, slash, and common control keys
    if (
      /^[0-9/]$/.test(e.key) || // Digits or slash
      e.key === 'Backspace' ||
      e.key === 'Delete' ||
      e.key === 'Tab' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight' ||
      e.key === 'Home' ||
      e.key === 'End'
    ) {
      return // Allow these keys
    }

    // Prevent all other keys, especially letters
    if (/[a-zA-Z]/.test(e.key)) {
      e.preventDefault()
    }
  }

  // Maneja el cambio en el input
  const handleChange = (rawValue) => {
    const numericValue = rawValue.replace(/[^0-9]/g, '') // Keep only digits
    let nuevoValor = maskMonthYear(numericValue)
    setInternalValue(nuevoValor)
    onChange(nuevoValor)

    // When the input reaches the full YYYY/MM format (7 chars), validate immediately
    // so the parent can enable/disable the submit button without waiting for blur
    if (nuevoValor.length === 7) {
      const validation = validateMonthYear(nuevoValor, minYear, maxYear)
      onValidate(validation.valid)
      if (validation.valid) {
        setStatus('success')
        setHintText('')
      } else {
        setStatus('error')
        setHintText(validation.message || '')
      }
    } else {
      // Reset validation state when the input is incomplete
      onValidate(false)
      setStatus('default')
      setHintText('Ingresá año y mes de las remuneraciones')
    }
  }

  // Maneja el blur y aplica la validación
  const handleBlur = (e) => {
    const blurValue = e?.target?.value ?? ''

    if (!/^[0-9/]*$/.test(blurValue)) {
      setInternalValue('')
      setStatus('default')
      setHintText('Ingresá año y mes de las remuneraciones')
      return
    }

    if (!blurValue) {
      setStatus('default')
      setHintText('Ingresá año y mes de las remuneraciones')
      return
    }

    const validation = validateMonthYear(internalValue, minYear, maxYear)
    onValidate(validation.valid)
    if (validation.valid) {
      setStatus('success')
      setHintText('')
    } else {
      setStatus('error')
      setHintText(validation.message || '')
    }
  }

  useEffect(() => {
    setInternalValue(value)
    onChange(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <Input
      type="text"
      label={label}
      isOptional={isOptional}
      isRequired={isRequired}
      info={info}
      value={internalValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      hints={hintsArray}
      status={status}
      onKeyDown={handleKeyDown}
      maxCharacters={7} /* YYYY/MM is 7 characters */
      classNameContainer={classNameContainer}
      classNameContainerInput={classNameContainerInput}
      className={className}
      {...props}
    />
  )
}

InputMonthYear.propTypes = {
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onValidate: PropTypes.func,
  value: PropTypes.string,
  minYear: PropTypes.number,
  maxYear: PropTypes.number,
  label: PropTypes.string,
  isOptional: PropTypes.bool,
  isRequired: PropTypes.bool,
  info: PropTypes.string,
  classNameContainer: PropTypes.string,
  classNameContainerInput: PropTypes.string,
  className: PropTypes.string,
  hints: PropTypes.arrayOf(
    PropTypes.shape({
      variant: PropTypes.oneOf(['info', 'error', 'success']),
      text: PropTypes.string,
    })
  ),
}

InputMonthYear.displayName = 'InputMonthYear'
// }
